/**
 * Every triangle of a loaded object, flattened into one array of world-space
 * positions — the input both of this port's ground shadows start from.
 *
 * Extracted from `planes/parked-aircraft.ts`, which had it to itself until
 * `flight/scene-shadows.ts` needed the same walk for the shadows an aeroplane
 * throws in flight. The two projections differ (one shears a parked aeroplane
 * along a measured low sun, the other drops a flying one along the mission's
 * own); the *collection* is identical, and it is the half with the rule in it.
 */

import * as THREE from "three/webgpu";

/**
 * **Only what is actually drawn.** `traverseVisible` rather than `traverse`,
 * because an enhanced `.glb` hides its modelled propeller whenever AD-012's disc
 * layer is drawing one (`flight/modelled-propeller.ts`) — and a shadow with a
 * two-metre propeller in it, thrown by blades that are not on screen, is exactly
 * the sort of thing a projection cannot be talked out of. The effect's own disc
 * casts no shadow either way; it is not geometry.
 *
 * World space, so the caller is indifferent to where a model-frame rotation was
 * applied — carried on the object's matrix for a baked mesh, baked into the
 * geometry for an enhanced one. An object with no parent therefore comes back in
 * its own root frame, which is what a caller building reusable geometry wants.
 */
export function worldTrianglePositions(object: THREE.Object3D): number[] {
  object.updateMatrixWorld(true);
  const out: number[] = [];
  const vertex = new THREE.Vector3();
  object.traverseVisible((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const geometry = child.geometry as THREE.BufferGeometry;
    const position = geometry.getAttribute("position") as THREE.BufferAttribute | undefined;
    if (!position) return;
    const index = geometry.getIndex();
    const count = index ? index.count : position.count;
    for (let i = 0; i < count; i++) {
      const v = index ? index.getX(i) : i;
      vertex.fromBufferAttribute(position, v).applyMatrix4(child.matrixWorld);
      out.push(vertex.x, vertex.y, vertex.z);
    }
  });
  return out;
}
